// src/components/NotificationBell.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    getUnreadCount,
    listNotifications,
    markAllRead,
    markRead,
} from '../api/notifications';
import { getActiveRole } from '../utils/auth';
import { resolveNotificationDestination } from '../utils/notificationDisplay';
import NotificationItem from './NotificationItem';

const POLL_INTERVAL_MS = 60_000;

const relativeTime = (iso, t) => {
    if (!iso) return '';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return t('notificationsPage.justNow');
    if (diff < 3600) return t('notificationsPage.minutesAgo', { count: Math.floor(diff / 60) });
    if (diff < 86400) return t('notificationsPage.hoursAgo', { count: Math.floor(diff / 3600) });
    return t('notificationsPage.daysAgo', { count: Math.floor(diff / 86400) });
};

const NotificationBell = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const [countStatus, setCountStatus] = useState('pending'); // pending | ok | error
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [actionError, setActionError] = useState('');
    const containerRef = useRef(null);

    const refreshCount = useCallback(async () => {
        try {
            const count = await getUnreadCount();
            setUnread(count);
            setCountStatus('ok');
        } catch {
            setCountStatus('error');
        }
    }, []);

    useEffect(() => {
        refreshCount();
        const id = setInterval(refreshCount, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [refreshCount]);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleOpen = async () => {
        const next = !open;
        setOpen(next);
        if (next) {
            setLoading(true);
            setLoadError(false);
            setActionError('');
            try {
                const data = await listNotifications();
                setItems(data);
                setUnread(data.filter((n) => !n.read).length);
                setLoadError(false);
            } catch {
                setItems([]);
                setLoadError(true);
            }
            finally { setLoading(false); }
        }
    };

    const handleItemOpen = async (item) => {
        if (!item.read) {
            try {
                await markRead(item.id);
                setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, read: true } : n)));
                setUnread((prev) => Math.max(0, prev - 1));
            } catch {
                setActionError(t('notificationBell.markReadFailed'));
            }
        }
        const resolvedLink = resolveNotificationDestination(item, getActiveRole());
        if (resolvedLink) {
            setOpen(false);
            navigate(resolvedLink);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await markAllRead();
            setItems((prev) => prev.map((n) => ({ ...n, read: true })));
            setUnread(0);
            setActionError('');
        } catch {
            setActionError(t('notificationBell.markReadFailed'));
        }
    };

    const cappedBadge = unread > 9 ? '9+' : unread;

    const notificationsPath = (() => {
        const role = (getActiveRole() || 'ADMIN').toUpperCase();
        if (role === 'MANAGER') return '/manager/notifications';
        if (role === 'COACH') return '/coach/notifications';
        // Parent/student WhatsApp prefs live at /notifications — inbox is separate.
        if (role === 'STUDENT') return '/student/inbox';
        if (role === 'PARENT') return '/parent/inbox';
        return '/admin/notifications';
    })();

    return (
        <div className="relative" ref={containerRef}>
            <button
                onClick={handleOpen}
                aria-label={unread > 0
                    ? t('notificationBell.ariaLabelUnread', { count: unread })
                    : t('notificationBell.ariaLabel')}
                className="relative flex h-9 w-9 items-center justify-center rounded-xl
                           bg-white/10 text-white transition-all hover:bg-white/20 active:scale-95"
            >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002
                           6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6
                           8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6
                           0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {countStatus === 'ok' && unread > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center
                                     justify-center rounded-full bg-red-500 px-1 text-[10px]
                                     font-bold leading-none tabular-nums tracking-tight text-white">
                        {cappedBadge}
                    </span>
                )}
                {countStatus === 'error' && (
                    <span
                        className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center
                                   justify-center rounded-full bg-amber-500 px-1 text-[10px]
                                   font-bold leading-none text-white"
                        title={t('notificationBell.countUnknown', { defaultValue: 'Unread count unavailable' })}
                    >
                        !
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 z-50 mt-3 w-[22rem] overflow-hidden rounded-2xl
                                border border-slate-200 bg-white text-slate-800
                                shadow-[0_18px_45px_rgba(15,23,42,0.16)] sm:w-[26rem]">
                    <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
                        <div className="flex items-center justify-between gap-2">
                            <div>
                                <p className="text-sm font-semibold text-slate-800">
                                    {t('notificationBell.title')}
                                </p>
                                <p className="mt-0.5 text-xs text-slate-500">
                                    {unread > 0
                                        ? t('notificationBell.unreadHint', { count: unread })
                                        : t('notificationBell.caughtUpHint')}
                                </p>
                            </div>
                            {unread > 0 && (
                                <button
                                    type="button"
                                    onClick={handleMarkAllRead}
                                    className="shrink-0 text-xs font-semibold text-primary-600 hover:text-primary-800"
                                >
                                    {t('notificationBell.markAllRead')}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="max-h-[440px] overflow-y-auto">
                        {(loadError || actionError) && (
                            <div className="border-b border-rose-100 bg-rose-50 px-4 py-2 text-xs text-rose-800" role="status">
                                {actionError || t('common.listLoadFailed')}
                            </div>
                        )}
                        {loading ? (
                            <div className="flex items-center justify-center py-10 text-slate-400">
                                <svg className="mr-2 h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10"
                                        stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                {t('selectSearch.loading')}
                            </div>
                        ) : loadError ? (
                            <div className="px-6 py-10 text-center">
                                <p className="text-sm font-semibold text-rose-700">{t('common.listLoadFailed')}</p>
                            </div>
                        ) : items.length === 0 ? (
                            <div className="px-6 py-10 text-center">
                                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                                            d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <p className="text-sm font-semibold text-slate-700">{t('notificationBell.empty')}</p>
                                <p className="mt-1 text-xs leading-relaxed text-slate-500">
                                    {t('notificationBell.emptyHint')}
                                </p>
                            </div>
                        ) : (
                            items.map((item) => (
                                <NotificationItem
                                    key={item.id}
                                    item={item}
                                    compact
                                    relativeTime={relativeTime(item.createdAt, t)}
                                    onOpen={handleItemOpen}
                                />
                            ))
                        )}
                    </div>

                    <div className="border-t border-slate-100 bg-slate-50 px-4 py-2.5 text-center">
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                                navigate(notificationsPath);
                            }}
                            className="text-sm font-semibold text-primary-600 hover:text-primary-800"
                        >
                            {t('notificationBell.viewAll')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
